import { CommonModule, DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  Output,
  Renderer2,
} from '@angular/core';
import {
  GenericListResponse,
  GenericPaginationResponse,
  GenericResponse,
  UserDetails,
  UserResponse,
  WorkflowPayload,
  WorkflowStep,
} from '../../../modules/shared/models/general.modal';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ApiServiceService } from '../../../modules/services/api-service.service';
import { MdaListResponse } from '../../../modules/services/utility-service.models';
import { MdaResponse } from '../../../modules/shared/models/mda.model';
import alertify from 'alertifyjs';
import { Workflow } from "../../../modules/shared/models/general.modal"
import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { UserDetailResponse } from '../../registration-page/registration-model';
import { CharLength } from '../../../modules/directives/char-length.directive';

@Component({
  selector: 'app-workflow-creation-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, CharLength],
  templateUrl: './workflow-creation-modal.component.html',
  styleUrl: './workflow-creation-modal.component.scss',
})
export class WorkflowCreationModalComponent
  implements AfterViewInit, OnDestroy
{
  @Input() mode: "CREATE" | "UPDATE" | "VIEW" = "CREATE"; 
  // @Input() isSubmitting = false;
  @Input() isOpen = false;
  @Input() workflow: Workflow;
  @Output() closed = new EventEmitter<void>();
  @Output() workflowSaved = new EventEmitter<WorkflowPayload>();
  @Output() workflowUpdated = new EventEmitter<void>();

  isSubmitting: boolean;
  isEditingStep: boolean;
  isLoadingSteps: boolean;
  workflowForm!: FormGroup;
  stepForm!: FormGroup;
  steps: WorkflowStep[] = [];
  // selectedStep: WorkflowStep | null;
  selectedStepIndex: number;

  workflowName = [
    { value: 'VENDOR_APPLICATION', label: 'Vendor Application' },
    // { value: 'ASSESSMENT_APPLICATION', label: 'Assessment' },
  ];

  stepNumbers = Array.from({length: 10}, (_, i) => i + 1);

  actions = [
    { value: 'NEXT', label: 'NEXT' },
    { value: 'FINAL', label: 'FINAL' },
  ];

  private readonly hostElement: HTMLElement;
  mdaList!: MdaResponse;
  adminList!: UserResponse;
  filteredAdmin: UserDetails[] = [];

  constructor(
    private fb: FormBuilder,
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
    @Inject(DOCUMENT) private readonly document: Document,
    private apiService:ApiServiceService
  ) {
    this.hostElement = this.elementRef.nativeElement;
  }
  

  ngAfterViewInit(): void {
    this.renderer.appendChild(this.document.body, this.hostElement);
  }

  ngOnDestroy(): void {
    if (this.hostElement.parentNode === this.document.body) {
      this.renderer.removeChild(this.document.body, this.hostElement);
    }
  }

  ngOnInit(): void {
    this.initializeForms();
    this.onSearchMda();
    this.onSearchAdmin();

    if (this.mode === "UPDATE" || this.mode === "VIEW") {
      this.workflowForm.patchValue(
        {
          name: this.workflow.name,
          mdaId: this.workflow.mdaId,
          description: this.workflow.description,
        }
      )
      this.fetchWorkflowSteps();
    }
  }

  initializeForms() {
    this.workflowForm = this.fb.group({
      name: ['', Validators.required],
      description: ['', Validators.required],
      mdaId: ['', Validators.required],
    });

    this.stepForm = this.fb.group({
      userId: ['', Validators.required],
      stepOrder: ['', Validators.required],
      onApproval: ['', Validators.required],
    });

    this.workflowForm.controls["mdaId"].valueChanges.subscribe({
      next:(value) => {
        if (!value || this.adminList?.data?.length === 0) return;
        this.onFilterAdmin(value);
      }
    })

    if (this.mode === "UPDATE") this.workflowForm.disable();
    if (this.mode === "VIEW") {
      this.workflowForm.disable();
      this.stepForm.disable();
    }
  }

  addStep(): void {
    if (this.stepForm.invalid) {
      this.stepForm.markAllAsTouched();
      return;
    }

    const { userId, stepOrder, onApproval } = this.stepForm.value;

    // Resolve display label for officer
    const officer = this.filteredAdmin.find((o) => o.id === userId);

    if (!officer) {
      alertify.error("Failed to get officer information");
      return;
    }

    if (this.mode === "CREATE") {
      const newSteps = [...this.steps];
      newSteps.push({
        userId: officer ? officer.id : userId,
        user: officer ? officer.full_name : userId,
        stepOrder: Number(stepOrder),
        onApproval,
        email: officer.email
      });
      newSteps.sort((a, b) => a.stepOrder - b.stepOrder);

      this.steps = newSteps;
      this.stepForm.reset();
    } else if (this.mode === "UPDATE")  {
      this.isLoadingSteps = true;
      this.apiService.post("/add/workflows/step",
        {
          description: this.workflow.description,
          mdaId: this.workflow.mdaId,
          name: this.workflow.name,
          steps: [
            {
              onApproval: onApproval,
              stepOrder: Number(stepOrder),
              userId: officer ? officer.id : userId,
            }
          ],
          workflowId: this.workflow.id
        }
      ).subscribe(
        {
          next: (response: GenericResponse) => {
            if (response.status !== "success") {
              alertify.error(response.message || "An error was encountered during step creation");
              return;
            }
            alertify.success(response.message || "Successfully added step");
            this.isLoadingSteps = false;
            this.exitStepEdit();
            this.fetchWorkflowSteps();
          },
          error: (error) => {
            if (error instanceof HttpErrorResponse) {
              alertify.error(error.message);
            } else {
              alertify.error(JSON.stringify(error))
            }
            this.isLoadingSteps = false;
            this.exitStepEdit();
            this.fetchWorkflowSteps();
          }
        }
      )
    }
  }

  completeStepEdit() {
    if (this.stepForm.invalid) {
      this.stepForm.markAllAsTouched();
      return;
    }

    const { userId, stepOrder, onApproval } = this.stepForm.value;
    // Resolve display label for officer
    const officer = this.filteredAdmin.find((o) => o.id === userId);

    if (!officer) {
      alertify.error("Failed to get officer information");
      return;
    }

    if (this.mode === "CREATE") {
      this.steps.splice(this.selectedStepIndex, 1, {
        userId: officer ? officer.id : userId,
        user: officer ? officer.full_name : userId,
        stepOrder: Number(stepOrder),
        onApproval,
        email: officer.email
      })
      this.steps.sort((a,b) => a.stepOrder - b.stepOrder);
      this.exitStepEdit();
    } else if (this.mode === "UPDATE") {
      const selectedStep = this.steps[this.selectedStepIndex];
      this.isLoadingSteps = true;
      this.apiService.put("/replace/workflows/step",
        {
          newEmail: officer.email,
          oldEmail: selectedStep.email,
          workflowId: this.workflow.id,
          step: selectedStep.stepOrder
        }
      ).subscribe(
        {
          next: (response: GenericResponse) => {
            if (response.status !== "success") {
              alertify.error(response.message || "An error was encountered during step update");
              return;
            }
            alertify.success(response.message || "Successfully updated step");
            this.isLoadingSteps = false;
            this.exitStepEdit();
            this.fetchWorkflowSteps();
          },
          error: (error) => {
            if (error instanceof HttpErrorResponse) {
              alertify.error(error.message);
            } else {
              alertify.error(JSON.stringify(error))
            }
            this.isLoadingSteps = false;
            this.exitStepEdit();
            this.fetchWorkflowSteps();
          }
        }
      )
    }
  }

  removeStep(index: number): void {
    if (this.mode === "CREATE") {
      this.steps.splice(index, 1);
    } else {
      const selectedStep = this.steps[index]
      this.isLoadingSteps = true;
      this.apiService.delete("/delete/workflows/step", {
        body: {
          email: selectedStep.email,
          workflowId: this.workflow.id,
          step: selectedStep.stepOrder
        }
      }).subscribe(
        {
          next: (response) => {
            if (response.status !== "success") {
              alertify.error(response.message || "An error was encountered during step deletion");
              return;
            }
            alertify.success(response.message || "Successfully removed step");
            this.isLoadingSteps = false;
            this.exitStepEdit();
            this.fetchWorkflowSteps();
          },
          error: (error) => {
            if (error instanceof HttpErrorResponse) {
              alertify.error(error.message);
            } else {
              alertify.error(JSON.stringify(error));
            }
            this.isLoadingSteps = false;
            this.exitStepEdit();
            this.fetchWorkflowSteps();
          }
        }
      )
    }
  }

  editStep(index: number): void {
    this.isEditingStep = true;
    this.selectedStepIndex = index;

    const selectedStep = this.steps[index];

    this.stepForm.patchValue({
      userId: selectedStep.userId,
      stepOrder: selectedStep.stepOrder,
      onApproval: selectedStep.onApproval,
    });
    
    if (this.mode === "UPDATE") {
      this.stepForm.controls["stepOrder"].disable();
      this.stepForm.controls["onApproval"].disable();
    }
  }

  exitStepEdit() {
    this.selectedStepIndex = null;
    this.isEditingStep = false;
    this.stepForm.reset();
    this.stepForm.enable();
  }

  saveWorkflow(): void {
    if (this.workflowForm.invalid) {
      this.workflowForm.markAllAsTouched();
      return;
    }

    const payload: WorkflowPayload = {
      ...this.workflowForm.value,
      steps: this.steps,
    };

    this.apiService.post('/create-workflows', payload).subscribe({
      next: (response: { message?: string }) => {
        this.workflowSaved.emit(payload);
         this.close();
        alertify.success(response.message || 'Workflow created successfully.');
      },
      error: (error) => {
        const message =
          typeof error === 'string'
            ? error
            : 'Unable to create workflow. Please try again.';
        alertify.error(message);
      },
      complete: () => {
        this.isSubmitting = false;
      }
    })
  }

  fetchWorkflowSteps() {
    if (!this.workflow) return;
    this.isLoadingSteps = true;

    const params: HttpParams = new HttpParams()
    .set("id", this.workflow.id);

    this.apiService.get('/workflows/approval-steps', {params}).subscribe({
      next: (response: GenericListResponse<WorkflowStep>) => {
        const data = response.data;
        if ("content" in data) this.steps = data.content.sort((a,b) => a.stepOrder - b.stepOrder);
        else this.steps = data.sort((a,b) => a.stepOrder - b.stepOrder);
      },
      error: (error) => {
        const message =
          typeof error === 'string'
            ? error
            : 'Unable to create workflow. Please try again.';
        alertify.error(message);
      },
      complete: () => {
        this.isLoadingSteps = false;
      }
    })
  }

  close(): void {
    this.closed.emit();
    if (this.mode === "UPDATE") {
      this.workflowUpdated.emit();
    };
    this.resetForms();
  }

  private resetForms(): void {
    this.workflowForm?.reset();
    this.stepForm?.reset();
    this.steps = [];
  }

  onSearchMda(): void {
    this.apiService.get("/mdas").subscribe({
      next: (res: MdaResponse) => {
        this.mdaList = res;
      },
      error: (err) => {
        console.error('Search failed', err);
      }
    });
  }

  onSearchAdmin(): void {
    this.apiService.get("/admin-users").subscribe({
      next: (res: UserResponse) => {
        this.adminList = res;
        
        const mdaId = this.workflowForm.controls["mdaId"].value;
        if (mdaId) this.onFilterAdmin(mdaId);
      },
      error: (err) => {
        console.error('Search failed', err);
      }
    });
  }

  onFilterAdmin(mdaId: string): void {
    // const q = mdaId.target.value.toLowerCase().trim();
    const q = mdaId.toLowerCase().trim();
    if (!q) {
      this.filteredAdmin = [...this.adminList?.data];
      return;
    }
    this.filteredAdmin = this.adminList?.data?.filter(
      (wf) =>
        wf.mda_id.toLowerCase().includes(q)
    );
  }
}
